import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type RefObject,
} from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { useOrganisationId } from '../../lib/session';
import { useDebouncedValue } from '../../lib/useDebouncedValue';
import { buildSearchResultItems, type SearchResultItem } from './buildSearchResultItems';
import { optionId, SearchResultsPanel, type SearchPanelStatus } from './SearchResultsPanel';

const DEBOUNCE_MS = 200;

function isTypingTarget(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLElement &&
    (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName))
  );
}

/** Focus search with "/" or Ctrl/⌘ K from anywhere that isn't a text field. */
function useSearchShortcut(inputRef: RefObject<HTMLInputElement>) {
  useEffect(() => {
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      const modK = (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k';
      const slash = event.key === '/' && !isTypingTarget(event.target);
      if (!modK && !slash) return;
      event.preventDefault();
      inputRef.current?.focus();
      inputRef.current?.select();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [inputRef]);
}

type HeaderSearchProps = {
  className?: string;
};

/** Organisation-wide search across screens, playlists, and content. */
export function HeaderSearch({ className }: HeaderSearchProps) {
  const { t } = useTranslation('layout');
  const organisationId = useOrganisationId();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();
  const [value, setValue] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  useSearchShortcut(inputRef);

  const typed = value.trim();
  const term = useDebouncedValue(typed, DEBOUNCE_MS);

  const searchQuery = useQuery({
    queryKey: ['search', organisationId, term],
    queryFn: () => api.search(organisationId, term),
    enabled: term.length > 0,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

  const items = useMemo(
    () => (searchQuery.data ? buildSearchResultItems(searchQuery.data) : []),
    [searchQuery.data],
  );

  useEffect(() => setActiveIndex(0), [items]);

  const settled = term === typed && !searchQuery.isFetching;
  const status: SearchPanelStatus = searchQuery.isError
    ? 'error'
    : settled
      ? 'ready'
      : 'searching';
  const showPanel = open && typed.length > 0;
  const showList = showPanel && status !== 'error' && items.length > 0;

  const choose = (item: SearchResultItem) => {
    navigate(item.to);
    setValue('');
    setOpen(false);
    inputRef.current?.blur();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      if (value) setValue('');
      else inputRef.current?.blur();
      return;
    }
    if (!showList) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((index) => (index + 1) % items.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => (index - 1 + items.length) % items.length);
    } else if (event.key === 'Enter') {
      const item = items[activeIndex];
      if (item) {
        event.preventDefault();
        choose(item);
      }
    }
  };

  return (
    <div
      className={['relative min-w-0', className].filter(Boolean).join(' ')}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
      }}
    >
      <span
        className="pointer-events-none absolute start-[15px] top-1/2 grid -translate-y-1/2"
        style={{ color: 'var(--gs-muted)' }}
      >
        <svg
          width="17"
          height="17"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          aria-hidden
        >
          <circle cx="11" cy="11" r="7" />
          <path d="m20 20-3.6-3.6" />
        </svg>
      </span>
      <input
        ref={inputRef}
        type="search"
        role="combobox"
        aria-label={t('search.label')}
        aria-autocomplete="list"
        aria-expanded={showList}
        aria-controls={showList ? listId : undefined}
        aria-activedescendant={showList ? optionId(listId, activeIndex) : undefined}
        placeholder={t('search.placeholder')}
        autoComplete="off"
        spellCheck={false}
        value={value}
        onChange={(event) => {
          setValue(event.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        className="w-full rounded-xl border py-3 ps-[42px] pe-3.5 font-sans text-[14.5px] outline-none"
        style={{
          color: 'var(--gs-ink)',
          background: 'var(--gs-field)',
          borderColor: 'var(--gs-line)',
        }}
      />
      {showPanel ? (
        <SearchResultsPanel
          listId={listId}
          items={items}
          activeIndex={activeIndex}
          status={status}
          term={typed}
          onHover={setActiveIndex}
          onChoose={choose}
        />
      ) : null}
    </div>
  );
}
